import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Braces,
  ClipboardPaste,
  ChevronDown,
  ChevronRight,
  Copy,
  Expand,
  FileJson,
  FileCode2,
  FilePlus2,
  FileText,
  Folder,
  FolderPlus,
  FolderOpen,
  LoaderCircle,
  Palette,
  PencilLine,
  RefreshCw,
  RotateCw,
  Save,
  Shrink,
  Trash2
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { ViewSwitcher } from '../../components/ui/ViewSwitcher';
import { selectTheme } from '../../redux/slices/appSlice';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Tooltip } from '../../components/ui/Tooltip';
import {
  runSandboxPreview,
  SANDBOX_PREVIEW_EXPECTED_DURATION_MS
} from '../../redux/slices/previewSlice';
import { fetchProjectById, saveProjectFiles } from '../../redux/slices/projectsSlice';
import { CodeEditor } from './CodeEditor';
import { PreviewLoadingExperience } from './PreviewLoadingExperience';
import { PreviewEmptyExperience } from './PreviewEmptyExperience';
import { PreviewErrorExperience } from './PreviewErrorExperience';
import { PreviewSessionInfo } from './PreviewSessionInfo';
import { usePreviewIframeGate } from './usePreviewIframeGate';

const getEditorLanguage = (filePath) => {
  if (filePath.endsWith('.tsx')) {
    return 'tsx';
  }

  if (filePath.endsWith('.ts')) {
    return 'ts';
  }

  if (filePath.endsWith('.jsx')) {
    return 'jsx';
  }

  if (filePath.endsWith('.js')) {
    return 'js';
  }

  if (filePath.endsWith('.json')) {
    return 'json';
  }

  if (filePath.endsWith('.css')) {
    return 'css';
  }

  if (filePath.endsWith('.html')) {
    return 'html';
  }

  return 'text';
};

const DIRECTORY_PLACEHOLDER_FILE = '.gitkeep';

const isDirectoryPlaceholderPath = (filePath) =>
  filePath === DIRECTORY_PLACEHOLDER_FILE || filePath.endsWith(`/${DIRECTORY_PLACEHOLDER_FILE}`);

const normalizePath = (value) =>
  String(value || '')
    .replace(/\\/g, '/')
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment && segment !== '.')
    .join('/');

const joinPath = (...parts) => normalizePath(parts.filter(Boolean).join('/'));

const getParentPath = (filePath) => {
  const normalizedPath = normalizePath(filePath);
  const parts = normalizedPath.split('/').filter(Boolean);
  return parts.slice(0, -1).join('/');
};

const getBaseName = (filePath) => {
  const normalizedPath = normalizePath(filePath);
  const parts = normalizedPath.split('/').filter(Boolean);
  return parts[parts.length - 1] || '';
};

const splitFileName = (filePath) => {
  const baseName = getBaseName(filePath);
  const lastDotIndex = baseName.lastIndexOf('.');

  if (lastDotIndex <= 0) {
    return { name: baseName, extension: '' };
  }

  return {
    name: baseName.slice(0, lastDotIndex),
    extension: baseName.slice(lastDotIndex)
  };
};

const listDirectoryFilePaths = (files, directoryPath) => {
  const normalizedDirectoryPath = normalizePath(directoryPath);
  const directoryPrefix = normalizedDirectoryPath ? `${normalizedDirectoryPath}/` : '';

  return Object.keys(files || {}).filter((filePath) =>
    normalizedDirectoryPath ? filePath.startsWith(directoryPrefix) : true
  );
};

const entryExists = (files, targetPath) => {
  const normalizedPath = normalizePath(targetPath);

  if (!normalizedPath) {
    return true;
  }

  if (Object.prototype.hasOwnProperty.call(files || {}, normalizedPath)) {
    return true;
  }

  const prefix = `${normalizedPath}/`;
  return Object.keys(files || {}).some((filePath) => filePath.startsWith(prefix));
};

const getDuplicatePath = (files, targetPath, type) => {
  const normalizedPath = normalizePath(targetPath);

  if (!entryExists(files, normalizedPath)) {
    return normalizedPath;
  }

  const parentPath = getParentPath(normalizedPath);
  const { name, extension } = splitFileName(normalizedPath);
  const baseName = type === 'directory' ? getBaseName(normalizedPath) : name;
  const extensionSuffix = type === 'directory' ? '' : extension;
  let duplicateIndex = 1;

  while (duplicateIndex < 1000) {
    const duplicateName =
      duplicateIndex === 1 ? `${baseName}-copy${extensionSuffix}` : `${baseName}-copy-${duplicateIndex}${extensionSuffix}`;
    const duplicatePath = joinPath(parentPath, duplicateName);

    if (!entryExists(files, duplicatePath)) {
      return duplicatePath;
    }

    duplicateIndex += 1;
  }

  return normalizedPath;
};

const moveEntry = (files, sourcePath, targetPath, type) => {
  const nextFiles = { ...files };
  const normalizedSourcePath = normalizePath(sourcePath);
  const normalizedTargetPath = normalizePath(targetPath);

  if (type === 'file') {
    nextFiles[normalizedTargetPath] = nextFiles[normalizedSourcePath];
    delete nextFiles[normalizedSourcePath];
    return nextFiles;
  }

  listDirectoryFilePaths(nextFiles, normalizedSourcePath).forEach((filePath) => {
    const suffix = filePath.slice(normalizedSourcePath.length);
    nextFiles[`${normalizedTargetPath}${suffix}`] = nextFiles[filePath];
    delete nextFiles[filePath];
  });

  return nextFiles;
};

const deleteEntry = (files, targetPath, type) => {
  const nextFiles = { ...files };
  const normalizedTargetPath = normalizePath(targetPath);

  if (type === 'file') {
    delete nextFiles[normalizedTargetPath];
    return nextFiles;
  }

  listDirectoryFilePaths(nextFiles, normalizedTargetPath).forEach((filePath) => {
    delete nextFiles[filePath];
  });

  return nextFiles;
};

const createEmptyFileContent = (filePath) => {
  const extension = getFileExtension(filePath);

  if (extension === 'json') {
    return '{\n  \n}\n';
  }

  return '';
};

const getDialogDefaultValue = ({ mode, type, path }) => {
  if (mode === 'rename') {
    return getBaseName(path);
  }

  return '';
};

const createFileTree = (paths) => {
  const root = [];

  paths.forEach((filePath) => {
    const isPlaceholder = isDirectoryPlaceholderPath(filePath);
    const parts = filePath.split('/').filter(Boolean);
    const visibleParts = isPlaceholder ? parts.slice(0, -1) : parts;
    let currentLevel = root;

    visibleParts.forEach((part, index) => {
      const existingNode = currentLevel.find((node) => node.name === part);
      const isFile = index === visibleParts.length - 1 && !isPlaceholder;

      if (existingNode) {
        currentLevel = existingNode.children || currentLevel;
        return;
      }

      const nextNode = {
        name: part,
        path: visibleParts.slice(0, index + 1).join('/'),
        type: isFile ? 'file' : 'directory',
        children: isFile ? undefined : []
      };

      currentLevel.push(nextNode);
      currentLevel.sort((left, right) => {
        if (left.type !== right.type) {
          return left.type === 'directory' ? -1 : 1;
        }

        return left.name.localeCompare(right.name);
      });
      currentLevel = nextNode.children || currentLevel;
    });
  });

  return root;
};

const getFileExtension = (filePath) => {
  const fileName = filePath.split('/').pop() || '';
  const extension = fileName.includes('.') ? fileName.split('.').pop() : '';
  return extension.toLowerCase();
};

const getFilePresentation = (filePath, isDark) => {
  const extension = getFileExtension(filePath);

  if (['js', 'jsx', 'ts', 'tsx'].includes(extension)) {
    return {
      Icon: Braces,
      iconClassName: 'text-[#4fc1ff]',
      badgeClassName: isDark ? 'bg-[#22374a] text-[#9cdcfe]' : 'bg-sky-100 text-sky-700',
      label: extension.toUpperCase()
    };
  }

  if (extension === 'json') {
    return {
      Icon: FileJson,
      iconClassName: 'text-[#ce9178]',
      badgeClassName: isDark ? 'bg-[#4b2d26] text-[#f2c5b4]' : 'bg-orange-100 text-orange-700',
      label: 'JSON'
    };
  }

  if (extension === 'css') {
    return {
      Icon: Palette,
      iconClassName: 'text-[#d7ba7d]',
      badgeClassName: isDark ? 'bg-[#4f4327] text-[#f2d89d]' : 'bg-amber-100 text-amber-700',
      label: 'CSS'
    };
  }

  if (extension === 'html') {
    return {
      Icon: FileText,
      iconClassName: 'text-[#c586c0]',
      badgeClassName: isDark ? 'bg-[#473047] text-[#e3b8e0]' : 'bg-fuchsia-100 text-fuchsia-700',
      label: 'HTML'
    };
  }

  return {
    Icon: FileCode2,
    iconClassName: isDark ? 'text-[#c5c5c5]' : 'text-slate-400',
    badgeClassName: isDark ? 'bg-[#3a3d41] text-[#d4d4d4]' : 'bg-slate-200 text-slate-700',
    label: extension ? extension.toUpperCase() : 'TXT'
  };
};

const normalizeEditableFiles = (files) =>
  Object.fromEntries(
    Object.entries(files || {}).filter(
      ([filePath, content]) => typeof filePath === 'string' && typeof content === 'string'
    )
  );

export const PreviewPanel = ({ project, panelMode = 'preview', onPanelModeChange }) => {
  const dispatch = useAppDispatch();
  const { t, language, localizeDigits } = useI18n();
  const viewMode = useAppSelector((state) => state.preview.viewMode);
  const theme = useAppSelector(selectTheme);
  const previewState = useAppSelector((state) => state.preview.byProjectId[project.id] || {});
  const frameRef = useRef(null);
  const wasLoadingRef = useRef(false);
  const iframePhaseStartedAtRef = useRef(null);
  const expirationRefreshStartedRef = useRef(false);
  const previewStatusRef = useRef('');
  const [hasIframeLoadedOnce, setHasIframeLoadedOnce] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [localIframeReloadKey, setLocalIframeReloadKey] = useState(0);
  const iframeReloadKey = localIframeReloadKey;
  const [showDeployComplete, setShowDeployComplete] = useState(false);
  const [deployProgress, setDeployProgress] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [editableFiles, setEditableFiles] = useState({});
  const [editorBaselineFiles, setEditorBaselineFiles] = useState({});
  const [editorFilesLoading, setEditorFilesLoading] = useState(false);
  const [editorFilesError, setEditorFilesError] = useState('');
  const [activeFilePath, setActiveFilePath] = useState('');
  const [selectedTreeItem, setSelectedTreeItem] = useState(null);
  const [expandedDirectories, setExpandedDirectories] = useState({});
  const [clipboardEntry, setClipboardEntry] = useState(null);
  const [saveState, setSaveState] = useState({ status: 'idle', error: '' });
  const [editorActionFeedback, setEditorActionFeedback] = useState({ type: 'idle', message: '' });
  const [contextMenu, setContextMenu] = useState(null);
  const [actionDialog, setActionDialog] = useState({
    mode: null,
    type: 'file',
    path: '',
    parentPath: '',
    value: '',
    error: ''
  });
  const resolvedPreviewUrl = previewState.previewUrl || project.previewUrl || '';
  const isSandboxLoading = previewState.status === 'loading';
  const iframePreviewUrl = isSandboxLoading ? '' : resolvedPreviewUrl;
  const projectFiles = project.files || null;
  const serializedEditorBaseline = useMemo(() => JSON.stringify(editorBaselineFiles), [editorBaselineFiles]);
  const allEditablePaths = useMemo(() => Object.keys(editableFiles), [editableFiles]);
  const filePaths = useMemo(
    () => allEditablePaths.filter((filePath) => !isDirectoryPlaceholderPath(filePath)),
    [allEditablePaths]
  );
  const fileTree = useMemo(() => createFileTree(allEditablePaths), [allEditablePaths]);
  const changedFiles = useMemo(
    () =>
      new Set(
        allEditablePaths.filter(
          (filePath) => (editableFiles[filePath] || '') !== (editorBaselineFiles[filePath] || '')
        )
      ),
    [allEditablePaths, editableFiles, editorBaselineFiles]
  );
  const activeFileContent = activeFilePath ? editableFiles[activeFilePath] || '' : '';
  const hasPreviewSource = Boolean(
    (projectFiles && Object.keys(projectFiles).length) || project.previewHtml?.trim() || resolvedPreviewUrl
  );
  const hasPreview = Boolean(resolvedPreviewUrl);
  const previewError = previewState.error || '';
  const expiresAtUtc = previewState.expiresAtUtc || project.previewExpiresAtUtc || null;
  const expiresAtTimestamp = expiresAtUtc ? new Date(expiresAtUtc).getTime() : null;
  const isSessionExpired = Boolean(expiresAtTimestamp && expiresAtTimestamp <= now);
  const shouldMountPreviewIframe = Boolean(iframePreviewUrl && !isSessionExpired);
  const {
    iframeRef,
    previewIframeKey,
    isIframeReady,
    awaitingIframeReady,
    handlePreviewIframeLoad,
    resetIframeGate
  } = usePreviewIframeGate({
    previewUrl: shouldMountPreviewIframe ? iframePreviewUrl : '',
    iframeReloadKey,
    isSandboxLoading,
    isSessionExpired,
    isGateActive: previewState.status !== 'failed'
  });
  const isPreviewBootstrapping =
    isSandboxLoading || (awaitingIframeReady && shouldMountPreviewIframe && !isIframeReady);
  const showPreviewLoadingExperience =
    previewState.status !== 'failed' && (isSessionExpired || isPreviewBootstrapping);
  const showFullPreviewLoading = showPreviewLoadingExperience && !hasIframeLoadedOnce;
  const isLoading = isSandboxLoading;
  const loadingTitleKey = isSessionExpired ? 'previewExpiredTitle' : 'previewLoadingTitle';
  const loadingPhase = isSessionExpired
    ? 'expired'
    : isSandboxLoading
      ? 'sandbox'
      : 'iframe';
  const isSaving = saveState.status === 'saving';
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(editableFiles) !== serializedEditorBaseline,
    [editableFiles, serializedEditorBaseline]
  );
  const isDark = theme === 'dark';
  const editorSurfaceClassName = isDark ? 'bg-[#1e1e1e] text-[#d4d4d4]' : 'bg-slate-50 text-slate-800';
  const editorChromeClassName = isDark
    ? 'border-[#313131] bg-[#181818] text-[#c5c5c5]'
    : 'border-slate-200 bg-white text-slate-500';
  const filesPaneClassName = isDark ? 'border-[#313131] bg-[#252526]' : 'border-slate-200 bg-slate-100/80';

  const loadEditorFiles = useCallback(async () => {
    setEditorFilesLoading(true);
    setEditorFilesError('');

    try {
      const fetchedProject = await dispatch(fetchProjectById(project.id)).unwrap();
      const files = normalizeEditableFiles(fetchedProject.files || {});

      setEditableFiles(files);
      setEditorBaselineFiles(files);
      setSaveState({ status: 'idle', error: '' });
    } catch (error) {
      setEditorFilesError(error?.message || t('editorLoadError'));
    } finally {
      setEditorFilesLoading(false);
    }
  }, [dispatch, project.id, t]);

  useEffect(() => {
    if (panelMode !== 'editor') {
      return;
    }

    loadEditorFiles();
  }, [loadEditorFiles, panelMode]);

  useEffect(() => {
    setExpandedDirectories((current) => {
      const nextState = { ...current };

      filePaths.forEach((filePath) => {
        const parts = filePath.split('/').filter(Boolean);

        parts.slice(0, -1).forEach((_, index) => {
          const directoryPath = parts.slice(0, index + 1).join('/');

          if (!(directoryPath in nextState)) {
            nextState[directoryPath] = true;
          }
        });
      });

      return nextState;
    });
  }, [filePaths]);

  useEffect(() => {
    if (!filePaths.length) {
      setActiveFilePath('');
      return;
    }

    if (!activeFilePath || !(activeFilePath in editableFiles)) {
      setActiveFilePath(filePaths[0]);
    }
  }, [activeFilePath, editableFiles, filePaths]);

  useEffect(() => {
    if (!selectedTreeItem?.path) {
      if (activeFilePath) {
        setSelectedTreeItem({ path: activeFilePath, type: 'file' });
      }

      return;
    }

    if (selectedTreeItem.type === 'file') {
      if (!(selectedTreeItem.path in editableFiles) || isDirectoryPlaceholderPath(selectedTreeItem.path)) {
        setSelectedTreeItem(activeFilePath ? { path: activeFilePath, type: 'file' } : null);
      }

      return;
    }

    if (!entryExists(editableFiles, selectedTreeItem.path)) {
      setSelectedTreeItem(activeFilePath ? { path: activeFilePath, type: 'file' } : null);
    }
  }, [activeFilePath, editableFiles, selectedTreeItem]);

  useEffect(() => {
    if (!contextMenu) {
      return undefined;
    }

    const handlePointerDown = () => {
      setContextMenu(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setContextMenu(null);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  const formatRemainingTime = useCallback(
    (targetTimestamp) => {
      if (!targetTimestamp) {
        return '';
      }

      const remainingMs = Math.max(0, targetTimestamp - now);
      const totalSeconds = Math.floor(remainingMs / 1000);
      const totalHours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const parts = [totalHours, minutes, seconds].map((value) => String(value).padStart(2, '0'));

      return localizeDigits(parts.join(':'));
    },
    [localizeDigits, now]
  );

  const runPreview = useCallback(
    (force = false, files = projectFiles) =>
      dispatch(
        runSandboxPreview({
          projectId: project.id,
          files,
          previewHtml: project.previewHtml,
          language,
          force
        })
      ),
    [dispatch, language, project.id, project.previewHtml, projectFiles]
  );

  const toggleFullscreen = async () => {
    if (!frameRef.current) {
      return;
    }

    if (document.fullscreenElement) {
      await document.exitFullscreen();
      setIsFullscreen(false);
      return;
    }

    await frameRef.current.requestFullscreen();
    setIsFullscreen(true);
  };

  const refreshIframe = useCallback(() => {
    if (!hasPreview) {
      return;
    }

    try {
      iframeRef.current?.contentWindow?.location?.reload();
    } catch (error) {
      setLocalIframeReloadKey((current) => current + 1);
    }
  }, [hasPreview, iframeRef]);

  const handleSaveFiles = async () => {
    const nextFiles = normalizeEditableFiles(editableFiles);

    if (!Object.keys(nextFiles).length) {
      return;
    }

    try {
      setSaveState({ status: 'saving', error: '' });
      await dispatch(saveProjectFiles({ projectId: project.id, files: nextFiles })).unwrap();
      setEditorBaselineFiles(nextFiles);
      await runPreview(true, nextFiles).unwrap();
      onPanelModeChange?.('preview');
      setSaveState({ status: 'succeeded', error: '' });
    } catch (error) {
      setSaveState({ status: 'failed', error: error.message || t('editorSaveError') });
    }
  };

  const toggleDirectory = useCallback(
    (directoryPath) => {
      setExpandedDirectories((current) => ({
        ...current,
        [directoryPath]: current[directoryPath] === false
      }));
    },
    []
  );

  const setActionFeedback = useCallback((type, message) => {
    setEditorActionFeedback({ type, message });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const closeActionDialog = useCallback(() => {
    setActionDialog({
      mode: null,
      type: 'file',
      path: '',
      parentPath: '',
      value: '',
      error: ''
    });
  }, []);

  const openActionDialog = useCallback(
    ({ mode, type, path = '', parentPath = '' }) => {
      const resolvedParentPath =
        parentPath ||
        (mode === 'create'
          ? selectedTreeItem?.type === 'directory'
            ? selectedTreeItem.path
            : selectedTreeItem?.type === 'file'
              ? getParentPath(selectedTreeItem.path)
              : getParentPath(activeFilePath)
          : getParentPath(path));

      setActionDialog({
        mode,
        type,
        path,
        parentPath: resolvedParentPath,
        value: getDialogDefaultValue({ mode, type, path }),
        error: ''
      });
      closeContextMenu();
    },
    [activeFilePath, closeContextMenu, selectedTreeItem]
  );

  const handleCreateEntry = useCallback(
    (type) => {
      openActionDialog({ mode: 'create', type });
    },
    [openActionDialog]
  );

  const handleRenameEntry = useCallback(() => {
    if (!selectedTreeItem?.path) {
      setActionFeedback('error', t('editorSelectEntryFirst'));
      return;
    }

    openActionDialog({
      mode: 'rename',
      type: selectedTreeItem.type,
      path: selectedTreeItem.path
    });
  }, [openActionDialog, selectedTreeItem, setActionFeedback, t]);

  const handleDeleteEntry = useCallback(() => {
    if (!selectedTreeItem?.path) {
      setActionFeedback('error', t('editorSelectEntryFirst'));
      return;
    }

    openActionDialog({
      mode: 'delete',
      type: selectedTreeItem.type,
      path: selectedTreeItem.path
    });
  }, [openActionDialog, selectedTreeItem, setActionFeedback, t]);

  const handleCopyEntry = useCallback(() => {
    if (!selectedTreeItem?.path) {
      setActionFeedback('error', t('editorSelectEntryFirst'));
      return;
    }

    const files =
      selectedTreeItem.type === 'file'
        ? { [selectedTreeItem.path]: editableFiles[selectedTreeItem.path] || '' }
        : Object.fromEntries(
            listDirectoryFilePaths(editableFiles, selectedTreeItem.path).map((filePath) => [filePath, editableFiles[filePath]])
          );

    setClipboardEntry({
      ...selectedTreeItem,
      files
    });
    setActionFeedback('success', t('editorCopySuccess'));
  }, [editableFiles, selectedTreeItem, setActionFeedback, t]);

  const handlePasteEntry = useCallback(() => {
    if (!clipboardEntry?.path || !clipboardEntry.type) {
      setActionFeedback('error', t('editorClipboardEmpty'));
      return;
    }

    const targetDirectory =
      selectedTreeItem?.type === 'directory'
        ? selectedTreeItem.path
        : selectedTreeItem?.type === 'file'
          ? getParentPath(selectedTreeItem.path)
          : getParentPath(activeFilePath);
    const nextBasePath = getDuplicatePath(
      editableFiles,
      joinPath(targetDirectory, getBaseName(clipboardEntry.path)),
      clipboardEntry.type
    );
    const nextFiles = { ...editableFiles };

    if (clipboardEntry.type === 'file') {
      nextFiles[nextBasePath] = clipboardEntry.files[clipboardEntry.path] || '';
      setActiveFilePath(nextBasePath);
      setSelectedTreeItem({ path: nextBasePath, type: 'file' });
    } else {
      Object.entries(clipboardEntry.files).forEach(([filePath, content]) => {
        const suffix = filePath.slice(clipboardEntry.path.length);
        nextFiles[`${nextBasePath}${suffix}`] = content;
      });
      setExpandedDirectories((current) => ({
        ...current,
        [nextBasePath]: true,
        ...(targetDirectory ? { [targetDirectory]: true } : {})
      }));
      setSelectedTreeItem({ path: nextBasePath, type: 'directory' });
    }

    setEditableFiles(nextFiles);
    setActionFeedback('success', t('editorPasteSuccess'));
  }, [activeFilePath, clipboardEntry, editableFiles, selectedTreeItem, setActionFeedback, t]);

  const submitActionDialog = useCallback(() => {
    if (!actionDialog.mode) {
      return;
    }

    if (actionDialog.mode === 'delete') {
      const nextFiles = deleteEntry(editableFiles, actionDialog.path, actionDialog.type);

      setEditableFiles(nextFiles);

      if (actionDialog.type === 'file') {
        if (activeFilePath === actionDialog.path) {
          const nextVisibleFilePath = Object.keys(nextFiles).find((filePath) => !isDirectoryPlaceholderPath(filePath)) || '';
          setActiveFilePath(nextVisibleFilePath);
        }
      } else if (activeFilePath === actionDialog.path || activeFilePath.startsWith(`${actionDialog.path}/`)) {
        const nextVisibleFilePath = Object.keys(nextFiles).find((filePath) => !isDirectoryPlaceholderPath(filePath)) || '';
        setActiveFilePath(nextVisibleFilePath);
      }

      setSelectedTreeItem(null);
      setActionFeedback('success', t('editorDeleteSuccess'));
      closeActionDialog();
      return;
    }

    const nextName = normalizePath(actionDialog.value);

    if (!nextName) {
      setActionDialog((current) => ({
        ...current,
        error: t('editorInvalidName')
      }));
      return;
    }

    if (actionDialog.mode === 'create') {
      const nextPath = joinPath(actionDialog.parentPath, nextName);

      if (entryExists(editableFiles, nextPath)) {
        setActionDialog((current) => ({
          ...current,
          error: t('editorPathExists', { path: nextPath })
        }));
        return;
      }

      setEditableFiles((current) => {
        if (actionDialog.type === 'directory') {
          return {
            ...current,
            [joinPath(nextPath, DIRECTORY_PLACEHOLDER_FILE)]: ''
          };
        }

        return {
          ...current,
          [nextPath]: createEmptyFileContent(nextPath)
        };
      });

      if (actionDialog.type === 'directory') {
        setExpandedDirectories((current) => ({
          ...current,
          [nextPath]: true,
          ...(actionDialog.parentPath ? { [actionDialog.parentPath]: true } : {})
        }));
        setSelectedTreeItem({ path: nextPath, type: 'directory' });
      } else {
        setActiveFilePath(nextPath);
        setSelectedTreeItem({ path: nextPath, type: 'file' });
      }

      setActionFeedback('success', t(actionDialog.type === 'file' ? 'editorFileCreated' : 'editorDirectoryCreated'));
      closeActionDialog();
      return;
    }

    const currentPath = actionDialog.path;
    const targetPath = joinPath(getParentPath(currentPath), nextName);

    if (targetPath === currentPath) {
      closeActionDialog();
      return;
    }

    if (actionDialog.type === 'directory' && targetPath.startsWith(`${currentPath}/`)) {
      setActionDialog((current) => ({
        ...current,
        error: t('editorRenameIntoSelf')
      }));
      return;
    }

    if (entryExists(editableFiles, targetPath)) {
      setActionDialog((current) => ({
        ...current,
        error: t('editorPathExists', { path: targetPath })
      }));
      return;
    }

    setEditableFiles((current) => moveEntry(current, currentPath, targetPath, actionDialog.type));

    if (actionDialog.type === 'file') {
      if (activeFilePath === currentPath) {
        setActiveFilePath(targetPath);
      }
    } else {
      if (activeFilePath === currentPath || activeFilePath.startsWith(`${currentPath}/`)) {
        setActiveFilePath(activeFilePath.replace(currentPath, targetPath));
      }
      setExpandedDirectories((current) => {
        const nextState = { ...current };

        Object.keys(current).forEach((key) => {
          if (key === currentPath || key.startsWith(`${currentPath}/`)) {
            const movedKey = key.replace(currentPath, targetPath);
            nextState[movedKey] = current[key];
            delete nextState[key];
          }
        });

        nextState[targetPath] = true;
        return nextState;
      });
    }

    setSelectedTreeItem({ path: targetPath, type: actionDialog.type });
    setActionFeedback('success', t('editorRenameSuccess'));
    closeActionDialog();
  }, [actionDialog, activeFilePath, closeActionDialog, editableFiles, setActionFeedback, t]);

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === frameRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);
    return () => {
      document.removeEventListener('fullscreenchange', syncFullscreenState);
    };
  }, []);

  useEffect(() => {
    if (isSandboxLoading) {
      wasLoadingRef.current = true;
      iframePhaseStartedAtRef.current = null;
      setShowDeployComplete(false);
      setHasIframeLoadedOnce(false);
    }
  }, [isSandboxLoading]);

  useEffect(() => {
    const previousStatus = previewStatusRef.current;
    const nextStatus = previewState.status || '';

    if (previousStatus === 'loading' && nextStatus === 'succeeded') {
      setLocalIframeReloadKey((current) => current + 1);
    }

    previewStatusRef.current = nextStatus;
  }, [previewState.status]);

  useEffect(() => {
    if (previewState.status === 'failed') {
      resetIframeGate();
    }
  }, [previewState.status, resetIframeGate]);

  useEffect(() => {
    setHasIframeLoadedOnce(false);
  }, [project.id]);

  useEffect(() => {
    if (isIframeReady) {
      iframePhaseStartedAtRef.current = null;
      setHasIframeLoadedOnce(true);
    }
  }, [isIframeReady]);

  useEffect(() => {
    if (showPreviewLoadingExperience) {
      return undefined;
    }

    if (wasLoadingRef.current && hasPreview && isIframeReady) {
      setShowDeployComplete(true);
      const timer = window.setTimeout(() => {
        setShowDeployComplete(false);
        wasLoadingRef.current = false;
      }, 700);

      return () => window.clearTimeout(timer);
    }

    if (!hasPreview) {
      wasLoadingRef.current = false;
    }
  }, [hasPreview, isIframeReady, showPreviewLoadingExperience]);

  useEffect(() => {
    if (!showPreviewLoadingExperience) {
      setDeployProgress(showDeployComplete ? 100 : 0);
      return undefined;
    }

    const expectedDurationMs = previewState.loadingExpectedDurationMs || SANDBOX_PREVIEW_EXPECTED_DURATION_MS;

    const updateProgress = () => {
      if (isSessionExpired && !isSandboxLoading) {
        const pulseProgress = 12 + ((Date.now() / 1400) % 1) * 16;
        setDeployProgress(pulseProgress);
        return;
      }

      if (isSandboxLoading) {
        const startedAt = previewState.loadingStartedAt || Date.now();
        const elapsedMs = Date.now() - startedAt;
        const sandboxProgress = Math.min(88, (elapsedMs / expectedDurationMs) * 88);
        setDeployProgress(sandboxProgress);
        return;
      }

      if (!iframePhaseStartedAtRef.current) {
        iframePhaseStartedAtRef.current = Date.now();
      }

      const iframeElapsedMs = Date.now() - iframePhaseStartedAtRef.current;
      const iframeProgress = 88 + Math.min(11, (iframeElapsedMs / 12000) * 11);
      setDeployProgress(iframeProgress);
    };

    updateProgress();
    const interval = window.setInterval(updateProgress, 250);

    return () => window.clearInterval(interval);
  }, [
    isSandboxLoading,
    isSessionExpired,
    previewState.loadingExpectedDurationMs,
    previewState.loadingStartedAt,
    showDeployComplete,
    showPreviewLoadingExperience
  ]);

  useEffect(() => {
    if (!expiresAtTimestamp) {
      expirationRefreshStartedRef.current = false;
      return undefined;
    }

    const updateNow = () => setNow(Date.now());
    updateNow();

    const intervalId = window.setInterval(updateNow, 1000);
    return () => window.clearInterval(intervalId);
  }, [expiresAtTimestamp]);

  useEffect(() => {
    if (!isSessionExpired) {
      expirationRefreshStartedRef.current = false;
      return;
    }

    if (isLoading || !hasPreviewSource || expirationRefreshStartedRef.current) {
      return;
    }

    expirationRefreshStartedRef.current = true;
    runPreview(true);
  }, [hasPreviewSource, isLoading, isSessionExpired, runPreview]);

  const previewRemainingTime = useMemo(() => {
    if (!expiresAtUtc) {
      return '';
    }

    return formatRemainingTime(expiresAtTimestamp);
  }, [expiresAtTimestamp, expiresAtUtc, formatRemainingTime]);

  const renderFileTree = useCallback(
    (nodes, depth = 0) =>
      nodes.map((node) => {
        if (node.type === 'directory') {
          const isExpanded = expandedDirectories[node.path] !== false;
          const isActiveBranch = activeFilePath === node.path || activeFilePath.startsWith(`${node.path}/`);
          const isSelected = selectedTreeItem?.path === node.path && selectedTreeItem?.type === 'directory';
          const hasChangedChild = filePaths.some((filePath) => changedFiles.has(filePath) && filePath.startsWith(`${node.path}/`));
          const directoryStateClassName = isDark
            ? isSelected || isActiveBranch
              ? 'bg-[#37373d] text-[#f3f3f3]'
              : 'text-[#cccccc] hover:bg-[#2a2d2e] hover:text-white'
            : isSelected || isActiveBranch
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
              : 'text-slate-600 hover:bg-white hover:text-slate-900';
          const chevronClassName = isDark
            ? isSelected || isActiveBranch
              ? 'text-[#c5c5c5]'
              : 'text-[#8b8b8b]'
            : isSelected || isActiveBranch
              ? 'text-slate-500'
              : 'text-slate-400';

          return (
            <div key={node.path}>
              <button
                type="button"
                onClick={() => {
                  setSelectedTreeItem({ path: node.path, type: 'directory' });
                  toggleDirectory(node.path);
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setSelectedTreeItem({ path: node.path, type: 'directory' });
                  setContextMenu({
                    x: event.clientX,
                    y: event.clientY,
                    path: node.path,
                    type: 'directory'
                  });
                }}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[13px] transition ${directoryStateClassName}`}
                style={{ paddingInlineStart: `${12 + depth * 16}px` }}
                dir="ltr"
              >
                {isExpanded ? (
                  <ChevronDown size={15} className={`shrink-0 ${chevronClassName}`} />
                ) : (
                  <ChevronRight size={15} className={`shrink-0 ${chevronClassName}`} />
                )}
                {isExpanded ? <FolderOpen size={15} className={`shrink-0 ${isDark ? 'text-[#dcb67a]' : 'text-amber-500'}`} /> : null}
                {!isExpanded ? <Folder size={15} className={`shrink-0 ${isDark ? 'text-[#dcb67a]' : 'text-amber-500'}`} /> : null}
                <span className="truncate font-medium">{node.name}</span>
                {hasChangedChild ? <span className="ms-auto h-2 w-2 rounded-full bg-emerald-400" /> : null}
              </button>
              {isExpanded ? <div className="mt-1">{renderFileTree(node.children || [], depth + 1)}</div> : null}
            </div>
          );
        }

        const isActive = node.path === activeFilePath;
        const isSelected = selectedTreeItem?.path === node.path && selectedTreeItem?.type === 'file';
        const { Icon, iconClassName, badgeClassName, label } = getFilePresentation(node.path, isDark);
        const isChanged = changedFiles.has(node.path);
        const fileStateClassName = isDark
          ? isSelected || isActive
            ? 'bg-[#37373d] text-[#ffffff]'
            : 'text-[#cccccc] hover:bg-[#2a2d2e] hover:text-white'
          : isSelected || isActive
            ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
            : 'text-slate-600 hover:bg-white hover:text-slate-900';
        const resolvedIconClassName = isActive
          ? isDark
            ? 'text-[#c5c5c5]'
            : 'text-slate-700'
          : iconClassName;
        const resolvedBadgeClassName = isActive
          ? isDark
            ? 'bg-[#4b4b4b] text-[#d4d4d4]'
            : 'bg-slate-900 text-white'
          : badgeClassName;

        return (
          <button
            key={node.path}
            type="button"
            onClick={() => {
              setActiveFilePath(node.path);
              setSelectedTreeItem({ path: node.path, type: 'file' });
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              setActiveFilePath(node.path);
              setSelectedTreeItem({ path: node.path, type: 'file' });
              setContextMenu({
                x: event.clientX,
                y: event.clientY,
                path: node.path,
                type: 'file'
              });
            }}
            className={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-[13px] transition ${fileStateClassName}`}
            style={{ paddingInlineStart: `${28 + depth * 16}px` }}
            dir="ltr"
          >
            <Icon size={15} className={`shrink-0 ${resolvedIconClassName}`} />
            <span className="truncate font-medium">{node.name}</span>
            <span
              className={`ms-auto rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] ${resolvedBadgeClassName}`}
            >
              {label}
            </span>
            {isChanged ? <span className="h-2 w-2 rounded-full bg-emerald-400" /> : null}
          </button>
        );
      }),
    [activeFilePath, changedFiles, expandedDirectories, filePaths, isDark, selectedTreeItem, toggleDirectory]
  );

  return (
    <section className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-white dark:bg-panel-strong">
      <div
        ref={frameRef}
        className="mx-auto flex h-full w-full min-w-0 max-w-full flex-col bg-white dark:bg-panel-strong"
      >
        <div className="relative z-30 flex h-12 shrink-0 items-center overflow-visible border-b border-neutral-200 bg-white px-2 dark:border-border dark:bg-panel-strong">
          <div className="flex w-full items-center gap-1 overflow-x-auto overflow-y-visible whitespace-nowrap">
            <div className="flex items-center">
              <Tooltip content={isFullscreen ? t('exitFullscreen') : t('fullscreen')} position="bottom">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                  disabled={panelMode !== 'preview' || viewMode !== 'desktop'}
                >
                  {isFullscreen ? <Shrink size={15} /> : <Expand size={15} />}
                </button>
              </Tooltip>
              <Tooltip content={t('refreshIframe')} position="bottom">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text"
                  onClick={refreshIframe}
                  aria-label={t('refreshIframe')}
                  disabled={panelMode !== 'preview' || !hasPreview}
                >
                  <RotateCw size={15} />
                </button>
              </Tooltip>
              <Tooltip content={t('updateSandboxTooltip')} position="bottom">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-muted dark:hover:bg-accent-soft dark:hover:text-text"
                  onClick={() => runPreview(true)}
                  aria-label={t('updateSandbox')}
                  disabled={isLoading || !hasPreviewSource}
                >
                  {isLoading ? <LoaderCircle size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                </button>
              </Tooltip>
            </div>
            <div className="ms-auto flex min-w-0 shrink-0 items-center justify-end gap-1">
              {panelMode === 'preview' ? (
                <>
                  <div className="hidden xl:flex">
                    <ViewSwitcher compact iconOnly />
                  </div>
                  {!showPreviewLoadingExperience ? (
                    <PreviewSessionInfo
                      previewUrl={resolvedPreviewUrl}
                      remainingTime={previewRemainingTime}
                    />
                  ) : null}
                </>
              ) : null}
              {panelMode === 'editor' ? (
                <Button
                  type="button"
                  className="h-8 min-h-0 rounded-lg px-2.5 text-xs"
                  onClick={handleSaveFiles}
                  disabled={isSaving || editorFilesLoading || !hasUnsavedChanges || !filePaths.length}
                >
                  {isSaving ? <LoaderCircle size={14} className="animate-spin" /> : <Save size={14} />}
                  <span className="hidden sm:inline">{t('updateSandbox')}</span>
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        {panelMode === 'editor' ? (
          <div className={`grid min-h-[420px] flex-1 grid-cols-1 gap-0 overflow-hidden sm:min-h-[560px] ${editorSurfaceClassName} lg:grid-cols-[minmax(0,1fr)_300px]`}>
            <div className={`flex min-h-0 flex-col border-b lg:border-b-0 lg:border-e ${editorChromeClassName}`}>
              <div
                className={`flex h-10 items-center border-b px-4 text-[11px] font-semibold uppercase tracking-[0.16em] ${editorChromeClassName}`}
                dir="ltr"
              >
                {activeFilePath || t('editorTab')}
              </div>
              <div className={`flex min-h-0 flex-1 flex-col ${editorSurfaceClassName}`}>
                {editorFilesLoading ? (
                  <div
                    className={`flex min-h-[420px] flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm ${
                      isDark ? 'bg-[#1e1e1e] text-[#8b949e]' : 'bg-slate-50 text-slate-500'
                    }`}
                  >
                    <LoaderCircle size={28} className="animate-spin" strokeWidth={2.4} />
                    <p>{t('editorLoadingTitle')}</p>
                  </div>
                ) : editorFilesError ? (
                  <div
                    className={`flex min-h-[420px] flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-sm ${
                      isDark ? 'bg-[#1e1e1e] text-[#f97583]' : 'bg-slate-50 text-rose-600'
                    }`}
                  >
                    <p>{editorFilesError}</p>
                    <Button type="button" variant="secondary" className="rounded-lg" onClick={loadEditorFiles}>
                      <RefreshCw size={14} />
                      {t('editorRetryLoad')}
                    </Button>
                  </div>
                ) : activeFilePath ? (
                  <CodeEditor
                    filePath={activeFilePath}
                    value={activeFileContent}
                    onChange={(nextValue) =>
                      setEditableFiles((current) => ({
                        ...current,
                        [activeFilePath]: nextValue
                      }))
                    }
                    onSave={handleSaveFiles}
                    language={getEditorLanguage(activeFilePath)}
                    ariaLabel={t('editorAriaLabel', { file: activeFilePath })}
                  />
                ) : (
                  <div
                    className={`flex min-h-[420px] flex-1 items-center justify-center p-6 text-center text-sm ${
                      isDark ? 'bg-[#1e1e1e] text-[#8b949e]' : 'bg-slate-50 text-slate-500'
                    }`}
                  >
                    {t('editorEmptyTitle')}
                  </div>
                )}
              </div>
            </div>
            <aside className={`flex min-h-0 flex-col border-t lg:border-t-0 ${filesPaneClassName}`}>
              <div
                className={`flex h-10 items-center justify-between gap-3 border-b px-4 ${editorChromeClassName}`}
                dir="ltr"
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em]">
                  {t('editorFiles')}
                </div>
                <div className="flex items-center gap-0">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 min-h-0 w-8 rounded-lg px-0 text-slate-600 dark:text-slate-300"
                    onClick={() => handleCreateEntry('file')}
                    title={t('editorAddFile')}
                    aria-label={t('editorAddFile')}
                  >
                    <FilePlus2 size={16} strokeWidth={2.2} className="shrink-0" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 min-h-0 w-8 rounded-lg px-0 text-slate-600 dark:text-slate-300"
                    onClick={() => handleCreateEntry('directory')}
                    title={t('editorAddDirectory')}
                    aria-label={t('editorAddDirectory')}
                  >
                    <FolderPlus size={16} strokeWidth={2.2} className="shrink-0" />
                  </Button>
                </div>
              </div>
              {editorActionFeedback.message ? (
                <div
                  className={`border-b px-3 py-2 text-xs ${
                    editorActionFeedback.type === 'error'
                      ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-200'
                  }`}
                  dir="ltr"
                >
                  {editorActionFeedback.message}
                </div>
              ) : null}
              <div
                className="max-h-[220px] overflow-auto px-2 py-2 [direction:ltr] lg:max-h-none lg:flex-1"
                onContextMenu={(event) => {
                  if (event.target !== event.currentTarget) {
                    return;
                  }

                  event.preventDefault();
                  setContextMenu({
                    x: event.clientX,
                    y: event.clientY,
                    path: '',
                    type: 'directory'
                  });
                }}
              >
                <div className="flex flex-col gap-1">{renderFileTree(fileTree)}</div>
              </div>
            </aside>
          </div>
        ) : showFullPreviewLoading && !shouldMountPreviewIframe ? (
          <PreviewLoadingExperience
            progress={deployProgress}
            phase={loadingPhase}
            titleKey={loadingTitleKey}
          />
        ) : shouldMountPreviewIframe ? (
          <div
            className={`relative z-0 flex min-h-[420px] flex-1 sm:min-h-[560px] ${
              viewMode === 'mobile' && !showFullPreviewLoading
                ? 'justify-center bg-[#F3F4F6] dark:bg-app'
                : 'flex-col bg-white dark:bg-panel-strong'
            }`}
          >
            {showFullPreviewLoading ? (
              <PreviewLoadingExperience
                className="min-h-[420px] flex-1 sm:min-h-[560px]"
                progress={deployProgress}
                phase={loadingPhase}
                titleKey={loadingTitleKey}
              />
            ) : null}
            <iframe
              key={previewIframeKey}
              ref={iframeRef}
              title={`${project.name} preview`}
              className={
                showFullPreviewLoading
                  ? 'pointer-events-none absolute h-px w-px overflow-hidden opacity-0'
                  : viewMode === 'mobile'
                    ? 'h-full w-full max-w-[390px] bg-white'
                    : 'min-h-[420px] w-full flex-1 bg-white sm:min-h-[560px]'
              }
              src={iframePreviewUrl}
              onLoad={handlePreviewIframeLoad}
              tabIndex={showFullPreviewLoading ? -1 : undefined}
              aria-hidden={showFullPreviewLoading}
            />
          </div>
        ) : previewError ? (
          <PreviewErrorExperience
            errorMessage={previewError}
            isRetrying={isSandboxLoading}
            onRetry={() => runPreview(true)}
          />
        ) : (
          <PreviewEmptyExperience hasPreviewSource={hasPreviewSource} />
        )}
      </div>
      {contextMenu
        ? createPortal(
        <div
          className="fixed z-[60] min-w-[210px] rounded-2xl border border-border bg-panel-strong p-1.5 shadow-2xl backdrop-blur"
          style={{
            left: Math.max(8, Math.min(contextMenu.x + 4, window.innerWidth - 236)),
            top: Math.max(8, Math.min(contextMenu.y + 4, window.innerHeight - 260))
          }}
          dir="ltr"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={(event) => event.preventDefault()}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-text transition hover:bg-white/60 dark:hover:bg-white/5"
            onClick={() => openActionDialog({ mode: 'create', type: 'file', parentPath: contextMenu.type === 'directory' ? contextMenu.path : getParentPath(contextMenu.path) })}
          >
            <FilePlus2 size={15} />
            {t('editorAddFile')}
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-text transition hover:bg-white/60 dark:hover:bg-white/5"
            onClick={() => openActionDialog({ mode: 'create', type: 'directory', parentPath: contextMenu.type === 'directory' ? contextMenu.path : getParentPath(contextMenu.path) })}
          >
            <FolderPlus size={15} />
            {t('editorAddDirectory')}
          </button>
          {contextMenu.path ? (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-text transition hover:bg-white/60 dark:hover:bg-white/5"
                onClick={() => handleRenameEntry()}
              >
                <PencilLine size={15} />
                {t('editorRename')}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-text transition hover:bg-white/60 dark:hover:bg-white/5"
                onClick={() => handleCopyEntry()}
              >
                <Copy size={15} />
                {t('editorCopy')}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-text transition hover:bg-white/60 disabled:opacity-40 dark:hover:bg-white/5"
            onClick={() => handlePasteEntry()}
            disabled={!clipboardEntry}
          >
            <ClipboardPaste size={15} />
            {t('editorPaste')}
          </button>
          {contextMenu.path ? (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-rose-600 transition hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10"
              onClick={() => handleDeleteEntry()}
            >
              <Trash2 size={15} />
              {t('editorDeleteEntry')}
            </button>
          ) : null}
        </div>
        , document.body)
        : null}
      <Modal
        title={
          actionDialog.mode === 'create'
            ? t(actionDialog.type === 'file' ? 'editorCreateFileTitle' : 'editorCreateDirectoryTitle')
            : actionDialog.mode === 'rename'
              ? t('editorRenameTitle')
              : t('editorDeleteTitle')
        }
        open={Boolean(actionDialog.mode)}
        onClose={closeActionDialog}
        panelClassName="max-w-md rounded-[1.75rem] p-4"
        contentClassName="mb-4"
        titleClassName="text-base"
      >
        {actionDialog.mode === 'delete' ? (
          <div className="space-y-4">
            <p className="text-sm leading-6 text-muted">
              {t(
                actionDialog.type === 'file' ? 'editorDeleteFileConfirm' : 'editorDeleteDirectoryConfirm',
                { name: getBaseName(actionDialog.path) }
              )}
            </p>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="ghost" className="px-3 py-2" onClick={closeActionDialog}>
                {t('cancel')}
              </Button>
              <Button type="button" variant="danger" className="px-3 py-2" onClick={submitActionDialog}>
                {t('delete')}
              </Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              submitActionDialog();
            }}
          >
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
                {t('editorTargetDirectory')}
              </div>
              <div className="rounded-xl border border-border bg-white/65 px-3 py-2 text-sm text-text dark:bg-slate-950/35">
                {actionDialog.parentPath || '/'}
              </div>
            </div>
            <Input
              autoFocus
              label={t(actionDialog.mode === 'create' ? 'editorNameOrPathLabel' : 'editorNewNameLabel')}
              className="rounded-xl px-3 py-2.5"
              value={actionDialog.value}
              onChange={(event) =>
                setActionDialog((current) => ({
                  ...current,
                  value: event.target.value,
                  error: ''
                }))
              }
              placeholder={t(actionDialog.type === 'file' ? 'editorCreateFilePrompt' : 'editorCreateDirectoryPrompt')}
              dir="ltr"
            />
            {actionDialog.error ? <p className="text-sm text-rose-600 dark:text-rose-300">{actionDialog.error}</p> : null}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="ghost" className="px-3 py-2" onClick={closeActionDialog}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="px-3 py-2">
                {t('save')}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </section>
  );
};
